/*
 * RolesPermissionsTab — the "Roles & Permissions" panel inside Agency
 * Settings. Lists all roles (built-in + custom), lets an admin toggle any
 * permission slug on/off, create new custom roles, delete customs, and
 * reset a built-in to its global template.
 *
 * Design choices:
 *   • Master/detail layout — role list on the left, permission matrix
 *     on the right. Keyboard-friendly, one edit at a time.
 *   • Permission matrix rendered as resource-row × action-column grid.
 *     `dashboard`, `security_log`, `audit_log` are view-only — their
 *     create/edit/delete cells render greyed-out em-dashes.
 *   • "Save changes" is explicit so a stray checkbox click doesn't
 *     silently rewrite a role. Dirty-state indicator on the save button.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, RotateCcw, Save, Eye, Sidebar as SidebarIcon } from "lucide-react";
import api, { errorMessage } from "../../lib/api";
import { ActionButton, GhostButton, PillBadge, inputClass } from "../Primitives";
import { Modal } from "../Modal";
import { useAuth } from "../../contexts/AuthContext";
import { SIDEBAR_LINKS } from "../../lib/sidebarConfig";
import { previewSidebarForRole } from "../../lib/sidebarPreview";

export function RolesPermissionsTab() {
  const [vocab, setVocab] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draftPerms, setDraftPerms] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [v, r] = await Promise.all([
        api.get("/permissions/vocabulary"),
        api.get("/roles"),
      ]);
      setVocab(v.data);
      setRoles(r.data || []);
      setSelectedId((prev) => prev || r.data?.[0]?.id || null);
    } catch (e) {
      toast.error(errorMessage(e, "Load failed"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = roles.find((r) => r.id === selectedId) || null;
  useEffect(() => {
    setDraftPerms(selected ? [...(selected.permissions || [])] : null);
  }, [selectedId, selected]);

  const isDirty = useMemo(() => {
    if (!selected || !draftPerms) return false;
    const a = new Set(selected.permissions || []);
    const b = new Set(draftPerms);
    if (a.size !== b.size) return true;
    for (const p of a) if (!b.has(p)) return true;
    return false;
  }, [selected, draftPerms]);

  const togglePermission = (slug) => {
    setDraftPerms((prev) => {
      if (!prev) return prev;
      const s = new Set(prev);
      if (s.has(slug)) s.delete(slug);
      else s.add(slug);
      return Array.from(s);
    });
  };

  const savePermissions = async () => {
    if (!selected || !isDirty) return;
    try {
      setSaving(true);
      await api.patch(`/roles/${selected.id}`, { permissions: draftPerms });
      toast.success(`${selected.display_name} permissions saved`);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!selected || !selected.is_builtin) return;
    if (!window.confirm(`Reset "${selected.display_name}" permissions to the built-in default? Any customizations will be lost.`)) return;
    try {
      await api.post(`/roles/${selected.id}/reset-to-default`);
      toast.success("Reset to default");
      await load();
    } catch (e) { toast.error(errorMessage(e, "Reset failed")); }
  };

  const deleteCustom = async () => {
    if (!selected || selected.is_builtin) return;
    if (!window.confirm(`Delete custom role "${selected.display_name}"?`)) return;
    try {
      await api.delete(`/roles/${selected.id}`);
      toast.success("Deleted");
      const remaining = roles.filter((r) => r.id !== selected.id);
      setSelectedId(remaining[0]?.id || null);
      await load();
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
  };

  const { startPreview } = useAuth();
  const navigate = useNavigate();
  const impersonateRole = () => {
    if (!selected) return;
    // Preview the *saved* role, not the unsaved draft — impersonating a
    // half-edited role would confuse the admin about what they're seeing.
    if (isDirty) {
      toast.error("Save your permission changes before previewing this role");
      return;
    }
    startPreview(selected);
    toast.success(`Previewing as ${selected.display_name}`, {
      description: "Use the yellow banner at the top of the page to exit preview.",
    });
    navigate("/");
  };

  if (loading || !vocab) {
    return <div className="p-8 text-zinc-500 scanlines">Loading roles &amp; permissions…</div>;
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Role list */}
      <div className="col-span-4 border border-[#27272A] bg-[#121212]" data-testid="roles-list">
        <div className="flex items-center justify-between p-3 border-b border-[#27272A]">
          <div className="label-mute">Roles ({roles.length})</div>
          <ActionButton data-testid="add-custom-role-btn" onClick={() => setCreating(true)}>
            <Plus className="size-3.5" /> Custom
          </ActionButton>
        </div>
        <div className="divide-y divide-[#27272A] max-h-[65vh] overflow-auto">
          {roles.map((r) => (
            <button
              key={r.id}
              data-testid={`role-row-${r.slug}`}
              onClick={() => setSelectedId(r.id)}
              className={`w-full text-left p-3 hover:bg-[#1a1a1a] ${selectedId === r.id ? "bg-[#1a1a1a] border-l-2 border-l-[#FF3B30]" : ""}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{r.display_name}</span>
                {r.is_builtin ? (
                  <PillBadge tone="default">Built-in</PillBadge>
                ) : (
                  <PillBadge tone="red">Custom</PillBadge>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
                {r.permissions?.length || 0} permissions
              </div>
              {r.description && (
                <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{r.description}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Permission matrix */}
      <div className="col-span-8 border border-[#27272A] bg-[#121212]">
        {selected ? (
          <PermissionMatrix
            role={selected}
            vocab={vocab}
            draftPerms={draftPerms || []}
            onToggle={togglePermission}
            onSave={savePermissions}
            onReset={resetToDefault}
            onDelete={deleteCustom}
            onImpersonate={impersonateRole}
            onReloadRoles={load}
            isDirty={isDirty}
            saving={saving}
          />
        ) : (
          <div className="p-8 text-zinc-500">Select a role from the left.</div>
        )}
      </div>

      {creating && (
        <CreateRoleModal
          onClose={() => setCreating(false)}
          onCreated={async (r) => { setCreating(false); await load(); setSelectedId(r.id); }}
          vocab={vocab}
        />
      )}
    </div>
  );
}

function PermissionMatrix({ role, vocab, draftPerms, onToggle, onSave, onReset, onDelete, onImpersonate, onReloadRoles, isDirty, saving }) {
  const set = useMemo(() => new Set(draftPerms), [draftPerms]);
  return (
    <div className="p-4 space-y-4" data-testid={`role-detail-${role.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-display font-black uppercase text-xl">{role.display_name}</div>
            {role.is_builtin ? <PillBadge tone="default">Built-in</PillBadge> : <PillBadge tone="red">Custom</PillBadge>}
          </div>
          <div className="text-xs text-zinc-400 mt-1">{role.description || "—"}</div>
          <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
            base_role: {role.base_role} · slug: <span className="font-mono">{role.slug}</span>
          </div>
          {/* iter296 — site visibility selector. Only surfaces on custom
              roles; built-in roles have hardcoded visibility that can't
              be flipped from here. Explains the difference right in the
              UI so admins don't wonder why their "scheduler" sees no
              sites. */}
          {!role.is_builtin && (
            <div
              className="mt-3 border border-[#27272A] bg-[#0A0A0A] p-3 space-y-1"
              data-testid={`role-site-scope-${role.slug}`}
            >
              <div className="label-mute">Sites &amp; Roster visibility</div>
              <div className="flex flex-wrap gap-2 mt-1">
                {[
                  { v: "agency",   label: "All sites in agency", hint: "Like Admin. Sees every site + every shift on the roster." },
                  { v: "assigned", label: "Assigned sites only", hint: "Like Supervisor. Only sees sites where they're a listed supervisor." },
                ].map(({ v, label, hint }) => {
                  // iter301 — Default to "agency" for legacy role docs
                  // missing `site_scope`. Matches the backend's new
                  // interpretation and the create-form default so the
                  // UI never lies about what the role actually sees.
                  const active = (role.site_scope || "agency") === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      data-testid={`role-site-scope-${role.slug}-${v}`}
                      onClick={async () => {
                        if (active) return;
                        try {
                          await api.patch(`/roles/${role.id}`, { site_scope: v });
                          toast.success(`Site visibility · ${label}`);
                          onReloadRoles?.();
                        } catch (e) { toast.error(errorMessage(e, "Update failed")); }
                      }}
                      title={hint}
                      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                        active
                          ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]"
                          : "border-[#27272A] hover:border-[#3F3F46] text-zinc-400"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                Only applies to <span className="font-mono">Sites</span> and <span className="font-mono">Roster</span>. Portal / client-tier scoping is unaffected.
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <GhostButton
            data-testid={`impersonate-role-${role.slug}`}
            onClick={onImpersonate}
            title="Reload the CRM through this role's permission lens"
            className="!border-[#FFCC00]/60 !text-[#FFCC00] hover:!bg-[#FFCC00]/10"
          >
            <Eye className="size-3.5" /> Impersonate
          </GhostButton>
          {role.is_builtin ? (
            <GhostButton data-testid="reset-defaults-btn" onClick={onReset}>
              <RotateCcw className="size-3.5" /> Reset to default
            </GhostButton>
          ) : (
            <GhostButton data-testid="delete-role-btn" onClick={onDelete}>
              <Trash2 className="size-3.5" /> Delete
            </GhostButton>
          )}
          <ActionButton
            data-testid="save-permissions-btn"
            onClick={onSave}
            disabled={!isDirty || saving}
            style={!isDirty ? { opacity: 0.5, pointerEvents: "none" } : undefined}
          >
            <Save className="size-3.5" /> {saving ? "Saving…" : (isDirty ? "Save changes" : "No changes")}
          </ActionButton>
        </div>
      </div>

      <div className="overflow-auto max-h-[60vh] border border-[#27272A]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#121212] border-b border-[#27272A]">
            <tr>
              <th className="px-3 py-2 text-left"><div className="label-mute">Resource</div></th>
              {vocab.actions.map((a) => (
                <th key={a} className="px-3 py-2 text-center w-24"><div className="label-mute">{a}</div></th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#27272A]">
            {vocab.resources.map((res) => {
              const isViewOnly = vocab.view_only_resources.includes(res);
              return (
                <tr key={res} className="hover:bg-[#1a1a1a]">
                  <td className="px-3 py-2 font-medium">{res.replace(/_/g, " ")}</td>
                  {vocab.actions.map((act) => {
                    const slug = `${res}:${act}`;
                    if (isViewOnly && act !== "view") {
                      return <td key={act} className="px-3 py-2 text-center text-zinc-700">—</td>;
                    }
                    const checked = set.has(slug);
                    return (
                      <td key={act} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          data-testid={`perm-${slug}`}
                          checked={checked}
                          onChange={() => onToggle(slug)}
                          className="accent-[#FF3B30] size-4 cursor-pointer"
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
        {draftPerms.length} of {vocab.permissions.length} permissions granted.
      </div>

      <SidebarPreviewPanel baseRole={role.base_role} permissions={draftPerms} />
    </div>
  );
}

/**
 * SidebarPreviewPanel — renders the live "if you save now, this role
 * will see these tabs" tree. Uses the SAME filter (`previewSidebarForRole`)
 * that Layout.jsx applies at render time so the preview never drifts
 * from the actual sidebar.
 *
 * Purely visual — collapsed groups still show their group header + count
 * so admins can see WHICH tabs are hidden vs invisible entirely.
 */
function SidebarPreviewPanel({ baseRole, permissions }) {
  const visible = useMemo(
    () => previewSidebarForRole(SIDEBAR_LINKS, baseRole, permissions),
    [baseRole, permissions],
  );
  const totalLeaves = useMemo(() => {
    let n = 0;
    for (const item of SIDEBAR_LINKS) {
      if (item.to) n += 1;
      if (Array.isArray(item.children)) n += item.children.length;
    }
    return n;
  }, []);
  const visibleLeaves = useMemo(() => {
    let n = 0;
    for (const item of visible) {
      if (item.to) n += 1;
      if (Array.isArray(item.children)) n += item.children.length;
    }
    return n;
  }, [visible]);
  return (
    <div
      data-testid="sidebar-preview-panel"
      className="border border-[#27272A] bg-[#0F0F0F] p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <SidebarIcon className="size-4 text-[#0A84FF]" />
        <div className="font-display uppercase tracking-widest text-xs text-white">
          Sidebar Preview
        </div>
        <PillBadge tone="default">{visibleLeaves} / {totalLeaves} tabs</PillBadge>
      </div>
      <div className="text-[10px] text-zinc-500">
        This is exactly what a user with this role will see in the left nav
        after you hit Save. Coarse role flags ({baseRole}) plus your
        checked permissions decide visibility.
      </div>
      {visible.length === 0 ? (
        <div className="text-xs text-zinc-500 italic px-2 py-4">
          No tabs visible. This role can log in but has no navigable pages.
        </div>
      ) : (
        <ul className="space-y-1 text-xs">
          {visible.map((item) => {
            const key = item.to || item.label;
            if (Array.isArray(item.children)) {
              return (
                <li key={key} data-testid={`sidebar-preview-group-${item.label}`}>
                  <div className="text-zinc-300 font-medium">▾ {item.label}</div>
                  <ul className="ml-4 mt-1 space-y-0.5 border-l border-[#27272A] pl-3">
                    {item.children.map((c) => (
                      <li
                        key={c.to}
                        className="text-zinc-500"
                        data-testid={`sidebar-preview-leaf-${c.to}`}
                      >
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            }
            return (
              <li key={key} className="text-zinc-300" data-testid={`sidebar-preview-leaf-${item.to}`}>
                • {item.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CreateRoleModal({ onClose, onCreated, vocab }) {
  const [form, setForm] = useState({
    display_name: "",
    description: "",
    base_role: "supervisor",
    site_scope: "agency",   // iter296 — default new roles to agency-wide site visibility; admins can flip to "assigned" if they want restricted scoping
    permissions: [],
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("Display name required");
    try {
      setBusy(true);
      const r = await api.post("/roles", form);
      toast.success("Custom role created");
      onCreated(r.data);
    } catch (e2) {
      toast.error(errorMessage(e2, "Create failed"));
    } finally { setBusy(false); }
  };

  return (
    <Modal title="New custom role" onClose={onClose} testId="new-role-modal">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <div className="label-mute mb-1">Display name *</div>
          <input
            data-testid="new-role-name"
            className={inputClass}
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="e.g., Regional Trainer"
            required
          />
        </div>
        <div>
          <div className="label-mute mb-1">Description</div>
          <input
            data-testid="new-role-desc"
            className={inputClass}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What does this role do?"
          />
        </div>
        <div>
          <div className="label-mute mb-1">Base role (for legacy compatibility)</div>
          <select
            data-testid="new-role-base"
            className={inputClass}
            value={form.base_role}
            onChange={(e) => setForm({ ...form, base_role: e.target.value })}
          >
            {["admin", "operations_manager", "chief_security_officer", "supervisor",
              "dispatcher", "regional_manager", "site_manager", "client_admin", "guard"].map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
            Determines legacy behaviour anywhere the code still checks role names directly.
          </div>
        </div>
        {/* iter296 — Sites/Roster visibility on the new role. Fix for the
            "scheduler custom role sees no sites" bug — this is now an
            explicit choice at creation time. */}
        <div>
          <div className="label-mute mb-1">Sites &amp; Roster visibility</div>
          <div className="flex gap-2 flex-wrap">
            {[
              { v: "agency",   label: "All sites in agency", hint: "Like Admin — sees every site + shift." },
              { v: "assigned", label: "Assigned sites only", hint: "Like Supervisor — only sites where they're listed." },
            ].map(({ v, label, hint }) => (
              <button
                key={v}
                type="button"
                data-testid={`new-role-scope-${v}`}
                onClick={() => setForm({ ...form, site_scope: v })}
                title={hint}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                  form.site_scope === v
                    ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]"
                    : "border-[#27272A] hover:border-[#3F3F46] text-zinc-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
            Editable later from the role&rsquo;s permission panel.
          </div>
        </div>
        <div className="text-xs text-zinc-400">
          Permissions can be edited after creation from the main panel.
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#27272A]">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <ActionButton type="submit" disabled={busy} data-testid="create-role-submit">
            {busy ? "Creating…" : "Create role"}
          </ActionButton>
        </div>
      </form>
    </Modal>
  );
}
